import { api } from "./index";

// Types
export interface AgendaTenderDocument {
  id: string;
  fileName: string;
  uploadedAt: string;
}

export interface AgendaItem {
  id: string;
  orderNo: number;
  subject: string;
  notes?: string | null;
  tenderId?: string | null;
  singleSourceId?: string | null;
  projectSummarySheetId?: string | null;
  tenderDocumentId?: string | null;
  tenderDocument?: AgendaTenderDocument | null;
  tender?: {
    id: string;
    referenceNo: string;
    description: string;
    category: string;
    stage: string;
  } | null;
  singleSource?: {
    id: string;
    formNumber: string;
    title: string;
    vendorName: string;
    proposedCost: number;
    status: string;
  } | null;
  projectSummarySheet?: {
    id: string;
    summarySheetNumber: string;
    projectName: string;
    status: string;
    estimatedCost: number | null;
  } | null;
  awardSheetLinks?: Array<{
    awardSheetId: string;
    awardSheet?: {
      id: string;
      awardSheetNumber: string | null;
      status: string;
    };
  }>;
  agendaReturnForm?: {
    id: string;
    formNumber: string | null;
    status: string;
  } | null;
}

export interface AwardSheetAgendaLink {
  awardSheetId: string;
  agendaItemId: string;
  sortOrder: number;
  agendaItem?: AgendaItem;
}

export interface MeetingAttendance {
  memberId: string;
  presence: boolean;
  allowVote?: boolean;
  isChairperson?: boolean;
  isSecretary?: boolean;
  member: {
    id: string;
    fullName: string;
    fullNameDhivehi?: string | null;
    email: string;
    role: string;
  };
}

export interface Meeting {
  id: string;
  meetingNumber: string;
  title: string;
  location: string;
  plannedStart: string;
  actualStart?: string;
  actualEnd?: string;
  status: 'DRAFT' | 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  createdAt: string;
  updatedAt: string;
  agendaItems?: AgendaItem[];
  attendances?: MeetingAttendance[];
}

export interface AgendaItemRequest {
  orderNo: number;
  subject: string;
  notes?: string | null;
  tenderId?: string | null;
  singleSourceId?: string | null;
  projectSummarySheetId?: string | null;
  tenderDocumentId?: string | null;
}

export interface CreateMeetingRequest {
  meetingNumber?: string;
  title: string;
  location: string;
  plannedStart: string;
  agendaItems?: AgendaItemRequest[];
  memberIds?: string[];
}

export interface AttendanceRequest {
  memberId: string;
  presence: boolean;
  allowVote?: boolean;
  isChairperson?: boolean;
  isSecretary?: boolean;
}

export interface SingleSourceRequest {
  meetingId: string;
  formNumber: string;
  vendor: string;
  cost: number;
  duration: number;
  justification: string;
  fileName: string;
  base64File: string;
}

export interface AgendaReturnFormRequest {
  meetingId: string;
  agendaItemId: string;
  reasonsForReturning: string;
  detailsOfAmendmentsRequired: string;
  allowedToResubmitWithAmendments: string;
}

export interface AgendaReturnFormApproval {
  id: string;
  memberId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  comments?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  member?: {
    id: string;
    fullName: string;
    fullNameDhivehi?: string | null;
    email: string;
    role: string;
  };
}

/** What a signer's approval stands for on an award sheet / return form. */
export type SigningRole = 'CHAIR' | 'SECRETARY' | 'MEMBER';

export interface AgendaReturnFormApprovalPending {
  id: string;
  agendaReturnFormId: string;
  memberId: string;
  status: string;
  signingAs?: SigningRole;
  agendaReturnForm?: AgendaReturnForm & {
    meeting?: { id: string; title: string; meetingNumber: string };
    agendaItem?: { id: string; orderNo: number; subject: string };
  };
}

export interface AgendaReturnForm {
  id: string;
  meetingId: string;
  agendaItemId: string;
  formNumber?: string | null;
  year: number;
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED';
  reasonsForReturning: string;
  detailsOfAmendmentsRequired: string;
  allowedToResubmitWithAmendments: string;
  tenderId?: string | null;
  singleSourceId?: string | null;
  projectSummarySheetId?: string | null;
  chairApprovedAt?: string | null;
  chairApprovedById?: string | null;
  secretarySignedAt?: string | null;
  secretarySignedById?: string | null;
  createdAt: string;
  updatedAt: string;
  meeting?: Meeting;
  agendaItem?: AgendaItem;
  tender?: AgendaItem['tender'];
  singleSource?: AgendaItem['singleSource'];
  projectSummarySheet?: AgendaItem['projectSummarySheet'];
  approvals?: AgendaReturnFormApproval[];
  chairApprovedBy?: { id: string; fullName: string; fullNameDhivehi?: string | null };
  secretarySignedBy?: { id: string; fullName: string; fullNameDhivehi?: string | null };
}

export interface AwardSheetRequest {
  meetingId: string;
  agendaItemIds: string[];
  tenderNumber?: string;
  tenderName: string;
  location: string;
  quotationNumber?: string;
  vendor: string;
  cost: number;
  duration: number;
  decisionText: string;
  justification: string;
  detail?: string;
  acknowledgement?: string;
}

export interface EndorsementRequest {
  awardSheetId: string;
  remarks?: string;
  fileName: string;
  pdfBase64: string;
}

export interface NotificationRequest {
  awardSheetId: string;
  referenceNo: string;
  recipient: string;
  quotationRef: string;
  totalPrice: number;
  deliveryDays: number;
  fileName: string;
  pdfBase64: string;
}

export interface CEOEndorsementItem {
  id?: string;
  agendaItemId: string;
  tenderQuotationNumber?: string;
  vendor: string;
  projectName: string;
  price: number;
  decision: string;
  detail?: string;
  order: number;
  agendaItem?: AgendaItem;
}

export interface CEOEndorsement {
  id: string;
  meetingId?: string;
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED';
  items: CEOEndorsementItem[];
  meeting?: Meeting;
  approvals?: Array<{
    id: string;
    stage: 'HEAD_OF_PROCUREMENT' | 'CEO';
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    comments?: string;
    approvedAt?: string;
    rejectedAt?: string;
    approver: {
      id: string;
      fullName: string;
      email: string;
      role: string;
    };
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface CEOEndorsementRequest {
  meetingId: string;
  items: Array<{
    agendaItemId: string;
    tenderQuotationNumber?: string;
    vendor: string;
    projectName: string;
    price: number;
    decision: string;
    detail?: string;
  }>;
}

export interface AwardSheet {
  id: string;
  docId?: string;
  meetingId: string;
  meeting?: Meeting;
  tenderId?: string | null;
  singleSourceId?: string | null;
  projectSummarySheetId?: string | null;
  tender?: {
    id: string;
    referenceNo: string;
    description: string;
    awardedVendor?: string | null;
  } | null;
  singleSource?: {
    id: string;
    formNumber: string;
    title: string;
    vendorName: string;
    projectName: string;
    projectLocation: string;
    proposedCost: number;
    proposedDuration: number;
  } | null;
  projectSummarySheet?: AgendaItem['projectSummarySheet'];
  agendaLinks?: AwardSheetAgendaLink[];
  tenderNumber: string;
  tenderName: string;
  location: string;
  quotationNumber?: string;
  vendor: string;
  proposedCost: number;
  proposedDuration: number;
  decisionText: string;
  justification: string;
  detail?: string;
  acknowledgement?: string;
  year: number;
  awardSheetNumber: string;
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED';
  signatories: AwardSheetSignatory[];
  approvals?: Array<{
    id: string;
    memberId: string;
    member: {
      id: string;
      fullName: string;
      fullNameDhivehi?: string | null;
      email: string;
      role: string;
    };
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    comments?: string;
    approvedAt?: string;
    rejectedAt?: string;
  }>;
  endorsements?: { id: string; endorsedAt: string }[];
  notifications?: { id: string }[];
  createdAt: string;
  updatedAt: string;
}

export interface AwardSheetSignatory {
  id: string;
  awardSheetId: string;
  memberId: string;
  member: {
    id: string;
    fullName: string;
    fullNameDhivehi?: string | null;
    role: string;
  };
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  isApproved: boolean;
  isRejected: boolean;
  signedAt?: string;
}

// Dashboard Stats Interface
export interface DashboardStats {
  pendingCount: number;
  completedCount: number;
  activeMeetings: number;
  totalAwardSheets: number;
  avgTurnaround: number;
}

export type MeetingDocumentCategory =
  | 'award-sheet'
  | 'single-source'
  | 'agenda-return-form'
  | 'project-summary'
  | 'ceo-endorsement'
  | 'tender-evaluation-report'
  | 'tender-tor'
  | 'agenda-tender-document';

export interface MeetingDocumentEntry {
  key: string;
  category: MeetingDocumentCategory;
  label: string;
  fileName: string;
  mimeType: string;
  source: 'generated-pdf' | 'minio';
  available: boolean;
  missingReason?: string;
  zipPath?: string;
}

export interface MeetingDocumentCategorySummary {
  id: MeetingDocumentCategory;
  label: string;
  count: number;
  availableCount: number;
}

export interface MeetingDocumentsManifest {
  meetingId: string;
  meetingNumber: string;
  meetingTitle: string;
  documents: MeetingDocumentEntry[];
  categories: MeetingDocumentCategorySummary[];
}

// Recent Activity Interface
export interface RecentActivity {
  id: string;
  type: 'meeting' | 'award_sheet' | 'endorsement' | 'notification' | 'tender';
  title: string;
  description: string;
  status: 'completed' | 'pending' | 'scheduled' | 'in_progress';
  timestamp: string;
  relatedId?: string;
  relatedType?: string;
}

// API Functions
export const bidCommitteeApi = {
  // Dashboard
  getDashboardStats: () => 
    api.get<DashboardStats>('/bid-committee/stats'),

  getRecentActivities: () => 
    api.get<RecentActivity[]>('/bid-committee/recent-activities'),

  // Meetings
  listMeetings: () => 
    api.get<Meeting[]>('/bid-committee/meetings'),
  
  getMeeting: (id: string) =>
    api.get<Meeting>(`/bid-committee/meetings/${id}`),

  getNextMeetingNumber: () =>
    api.get<{ meetingNumber: string }>('/bid-committee/meetings/next-number'),

  uploadAgendaTenderDocument: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<AgendaTenderDocument>('/bid-committee/meetings/agenda-documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  downloadAgendaTenderDocument: (documentId: string) =>
    api.get(`/bid-committee/meetings/agenda-documents/${documentId}/download`, {
      responseType: 'blob',
    }),
  
  createMeeting: (body: CreateMeetingRequest) => 
    api.post<Meeting>('/bid-committee/meetings', body),
  
  updateMeeting: (id: string, body: CreateMeetingRequest) =>
    api.put<Meeting>(`/bid-committee/meetings/${id}`, body),
  
  scheduleMeeting: (id: string) =>
    api.patch<Meeting>(`/bid-committee/meetings/${id}/schedule`),
  
  startMeeting: (id: string) => 
    api.patch<Meeting>(`/bid-committee/meetings/${id}/start`),
  
  finishMeeting: (id: string) => 
    api.patch<Meeting>(`/bid-committee/meetings/${id}/finish`),

  getMeetingDocuments: (meetingId: string) =>
    api.get<MeetingDocumentsManifest>(`/bid-committee/meetings/${meetingId}/documents`),

  downloadMeetingDocumentsZip: (meetingId: string, category?: MeetingDocumentCategory) =>
    api.get(`/bid-committee/meetings/${meetingId}/documents/download`, {
      params: category ? { category } : undefined,
      responseType: 'blob',
    }),

  downloadMeetingDocument: (meetingId: string, documentKey: string) =>
    api.get(
      `/bid-committee/meetings/${meetingId}/documents/${encodeURIComponent(documentKey)}/download`,
      { responseType: 'blob' }
    ),
  
  getEligibleTenders: () =>
    api.get<Array<{ id: string; referenceNo: string; description: string; category: string; stage: string }>>('/bid-committee/meetings/eligible/tenders'),
  
  getEligibleSingleSourceForms: () =>
    api.get<Array<{ id: string; formNumber: string; projectName: string; title: string; vendorName: string; proposedCost: number; status: string }>>('/bid-committee/meetings/eligible/single-source'),

  getEligibleProjectSummarySheets: () =>
    api.get<
      Array<{
        id: string;
        summarySheetNumber: string;
        projectName: string;
        status: string;
        estimatedCost: number | null;
        sheetDate: string;
      }>
    >('/bid-committee/meetings/eligible/project-summary-sheets'),

  // Attendance
  markAttendance: (meetingId: string, data: AttendanceRequest) => 
    api.post(`/bid-committee/attendance/${meetingId}`, data),

  // Single Source
  createSingleSource: (data: SingleSourceRequest) => 
    api.post('/bid-committee/single-sources', data),

  // Award Sheets
  createAwardSheet: (data: AwardSheetRequest) => 
    api.post<AwardSheet>('/bid-committee/award-sheets', data),
  
  updateAwardSheet: (id: string, data: AwardSheetRequest) =>
    api.put<AwardSheet>(`/bid-committee/award-sheets/${id}`, data),
  
  sendAwardSheetForApproval: (id: string) =>
    api.post<{ awardSheet: AwardSheet; approvals: any[] }>(`/bid-committee/award-sheets/${id}/send-for-approval`),
  
  listAwardSheets: () => 
    api.get<AwardSheet[]>('/bid-committee/award-sheets'),
  
  listAwardSheetsByMeeting: (meetingId: string) =>
    api.get<AwardSheet[]>(`/bid-committee/award-sheets/meetings/${meetingId}/award-sheets`),
  
  getAwardSheet: (id: string) => 
    api.get<AwardSheet>(`/bid-committee/award-sheets/${id}`),

  generateAwardSheetPdf: (id: string) =>
    api.get(`/bid-committee/award-sheets/${id}/pdf`, { responseType: 'blob' }),
  
  signAwardSheet: (id: string, data: { signaturePngBase64?: string; isApproved?: boolean; isRejected?: boolean }) => 
    api.post(`/bid-committee/award-sheets/${id}/sign`, data),

  getPendingSignatures: () => 
    api.get('/bid-committee/award-sheets/pending-signatures'),

  getNeedsEndorsement: () => 
    api.get('/bid-committee/award-sheets/needs-endorsement'),

  getPendingAwardSheetApprovals: () =>
    api.get<any[]>('/bid-committee/award-sheets/pending-approvals'),

  approveAwardSheetApproval: (id: string, data: { comments?: string }) =>
    api.post(`/bid-committee/award-sheets/approvals/${id}/approve`, data),

  rejectAwardSheetApproval: (id: string, data: { comments: string }) =>
    api.post(`/bid-committee/award-sheets/approvals/${id}/reject`, data),

  // Agenda return forms
  createAgendaReturnForm: (data: AgendaReturnFormRequest) =>
    api.post<AgendaReturnForm>('/bid-committee/agenda-return-forms', data),

  updateAgendaReturnForm: (id: string, data: Partial<AgendaReturnFormRequest>) =>
    api.put<AgendaReturnForm>(`/bid-committee/agenda-return-forms/${id}`, data),

  sendAgendaReturnFormForApproval: (id: string) =>
    api.post<AgendaReturnForm>(`/bid-committee/agenda-return-forms/${id}/send-for-approval`),

  listAgendaReturnForms: (params?: { meetingId?: string; status?: string }) =>
    api.get<AgendaReturnForm[]>('/bid-committee/agenda-return-forms', { params }),

  listAgendaReturnFormsByMeeting: (meetingId: string) =>
    api.get<AgendaReturnForm[]>(`/bid-committee/agenda-return-forms/meetings/${meetingId}`),

  getAgendaReturnForm: (id: string) =>
    api.get<AgendaReturnForm>(`/bid-committee/agenda-return-forms/${id}`),

  generateAgendaReturnFormPdf: (id: string) =>
    api.get(`/bid-committee/agenda-return-forms/${id}/pdf`, { responseType: 'blob' }),

  getPendingAgendaReturnFormApprovals: () =>
    api.get<AgendaReturnFormApprovalPending[]>('/bid-committee/agenda-return-forms/pending-approvals'),

  approveAgendaReturnFormApproval: (id: string, data?: { comments?: string; signaturePngBase64?: string }) =>
    api.post(`/bid-committee/agenda-return-forms/approvals/${id}/approve`, data ?? {}),

  rejectAgendaReturnFormApproval: (id: string, data: { comments: string }) =>
    api.post(`/bid-committee/agenda-return-forms/approvals/${id}/reject`, data),

  chairApproveAgendaReturnForm: (id: string, data?: { signaturePngBase64?: string }) =>
    api.post(`/bid-committee/agenda-return-forms/${id}/chair-approve`, data ?? {}),

  secretarySignAgendaReturnForm: (id: string, data?: { signaturePngBase64?: string }) =>
    api.post(`/bid-committee/agenda-return-forms/${id}/secretary-sign`, data ?? {}),

  // CEO Endorsements
  createCEOEndorsement: (data: CEOEndorsementRequest) =>
    api.post<CEOEndorsement>('/bid-committee/ceo-endorsements', data),

  updateCEOEndorsement: (id: string, data: CEOEndorsementRequest) =>
    api.put<CEOEndorsement>(`/bid-committee/ceo-endorsements/${id}`, data),

  getCEOEndorsement: (id: string) =>
    api.get<CEOEndorsement>(`/bid-committee/ceo-endorsements/${id}`),

  listCEOEndorsementsByMeeting: (meetingId: string) =>
    api.get<CEOEndorsement[]>(`/bid-committee/ceo-endorsements/meetings/${meetingId}/endorsements`),

  sendCEOEndorsementForApproval: (id: string) =>
    api.post<CEOEndorsement>(`/bid-committee/ceo-endorsements/${id}/send-for-approval`),

  approveCEOEndorsement: (id: string, data: { comments?: string }) =>
    api.post<CEOEndorsement>(`/bid-committee/ceo-endorsements/${id}/approve`, data),

  rejectCEOEndorsement: (id: string, data: { comments: string }) =>
    api.post<CEOEndorsement>(`/bid-committee/ceo-endorsements/${id}/reject`, data),

  getPendingCEOEndorsementApprovals: () =>
    api.get<any[]>('/bid-committee/ceo-endorsements/pending-approvals'),

  // Endorsements
  createEndorsement: (data: EndorsementRequest) => 
    api.post('/bid-committee/endorsements', data),

  // Notifications
  createNotification: (data: NotificationRequest) => 
    api.post('/bid-committee/notifications', data),
}; 